import cx from 'classnames';
import invariant from 'invariant';
import * as React from 'react';
import shallowCompare from 'react-addons-shallow-compare';

import focusNode from 'utils/focusNode';
import KeyConstants from 'constants/KeyConstants';
import Text from 'components/Text';
// @ts-ignore
import Token from 'components/Token';
import { KeyboardEventHandler, InputChangeHandler } from 'types/typescript/eventHandlers';

import './styles/Tokenizer.scss';

export type TokenItem = { id: number | string };
export type HeaderItem = { id: number | string; type: string };

export const ALL_TOKENS_ITEM: TokenItem = { id: '_all' };
export const HEADER_ITEM_ID: string = '_header';

type Props = {
  prompt?: string | null;
  inputClassName?: string;
  placeholder?: string | null;
  autoFocus?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
} & DefaultProps;

type DefaultProps = {
  value: TokenItem[];
  tokenRenderer: (item: TokenItem) => React.ReactElement;
  /* When the list of tokens changes */
  onChange: (item: TokenItem[]) => void;
  onDeselect: (item: TokenItem) => void;
  /* When the typed input changes */
  onInputChange: (text: string) => void;
  onInputKeyDown: KeyboardEventHandler;
};

type State = {
  inputValue: string;
  focused: boolean;
};

class Tokenizer extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    value: [],
    tokenRenderer: data => <Token>{data}</Token>,
    onChange: _value => {},
    onDeselect: _datum => undefined,
    onInputChange: (_s: String) => null,
    onInputKeyDown: (_e: React.KeyboardEvent) => {},
  };

  constructor(props: Props) {
    super(props);

    this.input = React.createRef();
    this.state = {
      inputValue: '',
      focused: false,
    };
  }

  shouldComponentUpdate(nextProps: Object, nextState: Object): boolean {
    return shallowCompare(this, nextProps, nextState);
  }

  input: React.RefObject<HTMLInputElement>;

  clearInputValue() {
    this.setState({ inputValue: '' }, () => this.props.onInputChange(''));
  }

  focusInput() {
    if (this.input && this.input.current) {
      focusNode(this.input.current);
    }
  }

  add(data: TokenItem | TokenItem[]) {
    const { value } = this.props;
    const newValue = Array.from(value);

    (Array.isArray(data) ? data : [data]).forEach(datum => {
      const index = value.indexOf(datum);
      if (index < 0) {
        newValue.push(datum);
      }
    });

    if (newValue.length !== value.length) {
      this.props.onChange(newValue);
    }
  }

  remove(datum: TokenItem) {
    const { value } = this.props;
    const index = value.indexOf(datum);
    if (index >= 0) {
      const newValue: TokenItem[] = value.slice(0, index).concat(value.slice(index + 1));
      this.props.onChange(newValue);
      this.props.onDeselect(datum);
    }
  }

  handleBlur = () => {
    this.setState({ focused: false });
    if (this.props.onBlur) {
      this.props.onBlur();
    }
  };

  handleFocus = () => {
    this.setState({ focused: true });
    if (this.props.onFocus) {
      this.props.onFocus();
    }
  };

  handleInputChange: InputChangeHandler = event => {
    const { target } = event;
    if (target instanceof HTMLInputElement) {
      const inputValue = target.value;
      this.setState({ inputValue });
      this.props.onInputChange(inputValue);
    }
  };

  handleInputKeyDown: KeyboardEventHandler = event => {
    const { keyCode } = event;
    switch (keyCode) {
      case KeyConstants.BACKSPACE:
        if (this.state.inputValue === '') {
          if (this.props.value.length > 0) {
            this.remove(this.props.value[this.props.value.length - 1]);
          }
        }
        break;
      default:
        break;
    }
    this.props.onInputKeyDown(event);
  };

  renderPrompt() {
    const { prompt } = this.props;
    if (!prompt) {
      return null;
    }
    return (
      <Text color="thunder" weight="normal">
        {prompt}
      </Text>
    );
  }

  renderToken = (datum: TokenItem) => {
    invariant(datum, 'renderToken called with no datum');
    const token = this.props.tokenRenderer(datum);
    return React.cloneElement(token, {
      key: datum.id,
      onRemove: () => this.remove(datum),
    });
  };

  renderInput() {
    const { autoFocus, inputClassName, value } = this.props;
    const { inputValue } = this.state;

    return (
      <div className="Tokenizer-input-container">
        {!inputValue && this.renderPlaceholder()}
        <input
          ref={this.input}
          className={cx(
            {
              'Tokenizer-input-has-value': !!inputValue || value.length > 0,
            },
            inputClassName
          )}
          type="text"
          value={inputValue}
          autoFocus={autoFocus}
          onChange={this.handleInputChange}
          onKeyDown={this.handleInputKeyDown}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
        />
      </div>
    );
  }

  renderPlaceholder() {
    const { placeholder } = this.props;
    if (!placeholder || this.props.value.length > 0) {
      return null;
    }
    return (
      <Text className="Tokenizer-placeholder" color="koala">
        {placeholder}
      </Text>
    );
  }

  render() {
    return (
      <div
        className={cx(
          'Tokenizer',
          {
            'Tokenizer-focused': this.state.focused,
          },
          this.props.className
        )}
        onClick={() => this.focusInput()}>
        <div className="Tokenizer-prompt-container">{this.renderPrompt()}</div>
        <div className="Tokenizer-token-inputlist">
          {this.props.value.map(this.renderToken)}
          {this.renderInput()}
        </div>
      </div>
    );
  }
}

export default Tokenizer;
